import { LoadingOverlay, Stack, Text, Title } from '@mantine/core'
import { getModuleInfo, getModuleNodes } from './ModuleUtil'
import ReportFooter from './ReportFooter'
import React, { useEffect, useState } from 'react'
import RightFloating from '../../components/RightFloating'
import Share from '../../components/Share'
import { ThemeConfig } from '../../utils/themeConfig'
import { ThemeProvider } from '../../components/ThemeProvider'
import ShareReportButton from '../../QueryReport/components/ShareReportButton'
import ModuleNavigation, { ModuleNavigationButton } from './ModuleNavigation'
import Stamp from './Stamp'
import { getStampStr } from './ReportUtils'
import { useViewMode, ViewModeProvider } from './ViewModeProvider'
import ViewModeToggleButton from './ViewModeToggleButton'
import { IconArrowUp } from '@tabler/icons-react'
import ImagePrintButton from '../../components/ImagePrintButton'
import ComplaintButton from '../../components/ComplaintButton'

interface MainProps {
  data: any
  isLoading: boolean
  theme: ThemeConfig
}

function MainContent({ data, isLoading, theme }: MainProps) {
  const [shareVisible, setShareVisible] = useState<boolean>(false)
  const [moduleNavOpened, setModuleNavOpened] = useState<boolean>(false)
  const { viewMode, toggleViewMode } = useViewMode()
  const [showBackToTop, setShowBackToTop] = useState<boolean>(false)

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300)
    }

    window.addEventListener('scroll', handleScroll)
    handleScroll()

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const { nodes, finalIndex } = getModuleNodes(data)
  const footerIndex = (finalIndex + 1).toString().padStart(2, '0')
  const modules = getModuleInfo(data)

  return (
    <div
      className="mx-auto max-w-4xl px-4 py-8 md:px-8 lg:px-12"
      style={{ backgroundColor: theme.backgroundColor }}
      id={'report'}
    >
      {isLoading && <LoadingOverlay zIndex={1000} visible overlayProps={{ radius: 'sm', blur: 2 }} />}
      <Stack className="gap-8">
        <Stack className="gap-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <Stack gap={4}>
              <Text size="xs" fw={600} style={{ letterSpacing: '0.18em', color: theme.accentTextColor }}>
                风险评估报告
              </Text>
              <Title
                order={2}
                className="text-gray-800"
                style={{ fontWeight: 700, lineHeight: 1.25, whiteSpace: 'nowrap', wordBreak: 'keep-all' }}
              >
                {data?.meta_info_title || '个人风险评估报告'}
              </Title>
            </Stack>
            <Stamp text={getStampStr(data)} />
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm text-gray-600">
            <span className="whitespace-nowrap">报告编号：{data?.meta_info_code || '—'}</span>
            <span className="whitespace-nowrap">生成时间：{data?.meta_info_date || '—'}</span>
          </div>
          <Text size="sm" className="text-gray-600" style={{ lineHeight: 1.6 }}>
            本报告非央行征信报告，基于用户授权信息生成，仅供业务参考，请结合实际情况综合判断。
          </Text>
        </Stack>
        <div>{nodes}</div>
        <ReportFooter sectionIndex={footerIndex} />
      </Stack>
      <RightFloating className="print:hidden">
        <Stack align={'flex-end'} gap={0}>
          <ModuleNavigationButton
            onClick={() => setModuleNavOpened(true)}
            themeConfig={theme}
            moduleCount={modules.length}
            className={'rounded-tl-lg'}
          />
          <ViewModeToggleButton viewMode={viewMode} onClick={toggleViewMode} themeConfig={theme} />
          <ImagePrintButton reportTitle={data?.meta_info_title} themeConfig={theme} className="hidden md:flex" />
          {showBackToTop ? (
            <div
              className="flex flex-col items-center justify-center cursor-pointer p-3 transition-all duration-300 hover:scale-105"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              style={{ backgroundColor: theme.backgroundColor }}
            >
              <IconArrowUp size={26} color={theme.primaryColor} />
              <span className="text-gray-500 text-xs font-medium mt-1">回到顶部</span>
            </div>
          ) : (
            <ShareReportButton
              key={'share'}
              onClick={() => {
                setShareVisible(true)
              }}
              themeConfig={theme}
            />
          )}
          <ComplaintButton themeConfig={theme} className="rounded-bl-lg" />
        </Stack>
      </RightFloating>
      <div className="print:hidden" data-export-hidden>
        <ModuleNavigation data={data} themeConfig={theme} opened={moduleNavOpened} setOpened={setModuleNavOpened} />
      </div>
      <Share shareVisible={shareVisible} setShareVisible={setShareVisible} />
    </div>
  )
}

export default function Main({ data, isLoading, theme }: MainProps) {
  return (
    <ThemeProvider theme={theme}>
      <ViewModeProvider>
        <MainContent data={data} isLoading={isLoading} theme={theme} />
      </ViewModeProvider>
    </ThemeProvider>
  )
}
